/** Angular imports */
import { afterNextRender, Component, effect, inject, signal, untracked } from '@angular/core';
import { catchError, EMPTY, forkJoin, of, Subscription } from 'rxjs';

/** Core imports */
import {
    ApiPagination,
    ApiPlaylist,
    ApiSinglePlaylist,
    LicensesService,
    PlaylistsService,
} from '@core';
import { AuthService } from '@core/services/auth/auth.service';

/** Component imports */
import {
    type AssignLicenseForm,
    AssignLicenseModalComponent,
} from '@features/playlists/components/ui';
import { ThumbnailItemComponent } from '@ntv360/component-pantry';
import {
    BaseQueryParams,
    ButtonComponent,
    FilterDropdownComponent,
    TableToolbarComponent,
} from '@shared';
import { EmptyComponent, ThumbnailSkeletonComponent } from '@shared/components';
import { AddContentComponent } from './components/add-content/add-content.component';

/** Local imports */
import { ActivatedRoute, Router } from '@angular/router';
import { UiThumbnailContent } from '@features/media-library/interfaces';
import {
    PLAYLIST_FILTERS_GROUP,
    TOOLBAR_BUTTONS_SINGLE_PLAYLIST,
    VIEW_CONTROLS_BUTTON,
} from '@features/playlists/constants';
import { MainLayoutStore } from '@layouts/main-layout';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { CopyDirective } from '@shared/directives';
import { FileSizePipe } from '@shared/pipes';
import { fetchAllBatch, getContentType, Logger, withLoading } from '@shared/utils';

@Component({
    selector: 'app-single-playlist',
    imports: [
        ButtonComponent,
        TableToolbarComponent,
        FilterDropdownComponent,
        AddContentComponent,
        AssignLicenseModalComponent,
        ConfirmationModalComponent,
        CopyDirective,
        EmptyComponent,
        ThumbnailItemComponent,
        ThumbnailSkeletonComponent,
    ],
    templateUrl: './single-playlist.component.html',
    styleUrl: './single-playlist.component.scss',
})
export class SinglePlaylistComponent {
    /** Service in managing playlist and API calls */
    private playlistsService = inject(PlaylistsService);

    /** Service in managing licenses and API calls */
    private licensesService = inject(LicensesService);

    /** Comment later */
    private route = inject(ActivatedRoute);

    /** Handles navigation and routing logic. */
    private router = inject(Router);

    /** Provides role/identity checks for dealer ownership enforcement. */
    private authService = inject(AuthService);

    /** Layout constants */
    protected readonly toolbarButtons = TOOLBAR_BUTTONS_SINGLE_PLAYLIST;

    /** Content filtering constants */
    protected readonly contentFiltersGroup = PLAYLIST_FILTERS_GROUP;

    /** Holds the playlist details */
    protected playlist = signal<ApiSinglePlaylist | null>(null);

    /** Resolved assigned licenses for the playlist, pairing source ID with its license key. */
    protected assignedLicenses = signal<{ id: string; licenseKey: string }[]>([]);

    /** Loading state for assigned licenses resolution. */
    protected isLoadingLicenses = signal<boolean>(false);

    /** Holds the contents data */
    protected contents = signal<UiThumbnailContent[]>([]);

    /** View controls buttons. Eg list view or grid view */
    protected readonly viewControls = VIEW_CONTROLS_BUTTON;

    /** Controls the visibility of the add content modal */
    protected showAddContentModal = signal<boolean>(false);

    /** Controls the loading state of the contents */
    protected isLoadingContents = signal<boolean>(false);

    /** Query params for playlist contents */
    protected queryParams = signal<BaseQueryParams>({
        limit: 40,
        page: 1,
    });

    /** Pagination state for the current data set. */
    protected paginationMeta = signal<ApiPagination>({
        totalCount: 0,
        page: 1,
        limit: 40,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
    });

    /** Holds whether it's currently loading more contents */
    protected isLoadingMoreContents = signal<boolean>(false);

    /** Subscription instance to cancel previous in-flight requests when searching or paginating */
    private loadContentsSubscription?: Subscription;

    /** Holds the selected content type to create and controls the add content button disabled state */
    protected selectedContentType = signal<'single' | 'block' | null>(null);

    /** Controls visibility of the Assign License modal. */
    protected showAssignLicenseModal = signal<boolean>(false);

    /**
     * License IDs already assigned to any playlist (this one or others).
     * Passed to the assign-license modal so a license can only ever live on a single playlist.
     */
    protected assignedLicenseIdsAcrossPlaylists = signal<string[]>([]);

    /** Controls visibility of the content filter dropdown in the toolbar. */
    protected showFilterBy = signal<boolean>(false);

    /** Search term used to filter playlist contents by asset name. */
    protected searchTerm = signal<string>('');

    /** Controls visibility of the assign result confirmation modal. */
    protected showAssignResultModal = signal<boolean>(false);

    /** Controls visibility of the remove-content confirmation modal. */
    protected showRemoveContentModal = signal<boolean>(false);

    /** Holds the playlist content item awaiting remove confirmation. */
    protected contentToRemove = signal<UiThumbnailContent | null>(null);

    /** True while the bulk-delete request for the selected content is in flight. */
    protected isRemovingContent = signal<boolean>(false);

    /** Controls visibility of the post-delete success confirmation modal. */
    protected showRemoveSuccessModal = signal<boolean>(false);

    /** Configuration for the assign result confirmation modal. */
    protected assignResultModalConfig = signal<{
        type: 'success' | 'error';
        title: string;
        message: string;
    }>({
        type: 'success',
        title: '',
        message: '',
    });

    constructor() {
        effect(() => {
            const search = this.searchTerm();
            const queryParams = this.queryParams();
            untracked(() => {
                const page = queryParams.page ?? 1;
                this.loadContents(search, page > 1);
            });
        });
        afterNextRender(() => {
            MainLayoutStore.setBreadcrumb('Single Playlist');
            this.loadPlaylistDetails();
        });

        effect(() => {
            if (this.showAssignLicenseModal()) {
                untracked(() => this.loadAssignedLicenseIdsAcrossPlaylists());
            }
        });
    }

    /**
     * Receives the debounced search value from the toolbar searchbar.
     *
     * @param value - Latest search term from the searchbar.
     */
    protected onSearchValueChange(value: string): void {
        this.queryParams.update((params) => ({ ...params, page: 1 }));
        this.searchTerm.set((value ?? '').trim());
    }

    /**
     * Opens the add content stepper modal directly with single content as the default type.
     */
    protected onOpenAddContentModal(): void {
        this.selectedContentType.set('single');
        this.showAddContentModal.set(true);
    }

    /**
     * Handles a remove-content click from the always-visible delete icon on each
     * grid thumbnail. Stores the target item and opens the confirmation modal.
     *
     * @param item - Mapped UI thumbnail content selected for removal.
     */
    protected onRequestRemoveContent(item: UiThumbnailContent): void {
        this.contentToRemove.set(item);
        this.showRemoveContentModal.set(true);
    }

    /**
     * Confirms the pending remove and POSTs to the playlist contents bulk-delete
     * endpoint with a single id. Refreshes the grid on success and closes the
     * modal in both success and error paths.
     */
    protected onConfirmRemoveContent(): void {
        const item = this.contentToRemove();
        const playlistId = this.route.snapshot.paramMap.get('id');
        const contentId = item?.contentId;

        if (!playlistId || !contentId) {
            this.showRemoveContentModal.set(false);
            this.contentToRemove.set(null);
            return;
        }

        this.playlistsService
            .bulkDeletePlaylistContents(playlistId, { contentIds: [contentId] })
            .pipe(
                withLoading(this.isRemovingContent),
                catchError((err: unknown) => {
                    Logger.error('SinglePlaylistComponent', 'Failed to remove content', err);
                    return EMPTY;
                }),
            )
            .subscribe({
                next: () => {
                    this.queryParams.update((params) => ({ ...params, page: 1 }));
                    this.showRemoveSuccessModal.set(true);
                },
                complete: () => {
                    this.showRemoveContentModal.set(false);
                    this.contentToRemove.set(null);
                },
            });
    }

    /** Closes the post-delete success confirmation modal. */
    protected onRemoveSuccessModalClose(): void {
        this.showRemoveSuccessModal.set(false);
    }

    /** Closes the remove-content modal and clears the pending target. */
    protected onCancelRemoveContent(): void {
        this.showRemoveContentModal.set(false);
        this.contentToRemove.set(null);
    }

    /**
     * Handles the assign event from the Assign License modal.
     * Posts the selected license to the playlist license assignment endpoint.
     *
     * @param form - Selected license payload from the modal.
     */
    protected onAssignLicense(form: AssignLicenseForm): void {
        const playlistId = this.route.snapshot.paramMap.get('id');
        if (!playlistId || !form.licenseId) {
            this.showAssignLicenseModal.set(false);
            return;
        }

        this.playlistsService
            .assignLicensesToPlaylist(playlistId, { licenseIds: [form.licenseId] })
            .pipe(
                catchError((err: unknown) => {
                    Logger.error(
                        'SinglePlaylistComponent',
                        'Failed to assign license to playlist',
                        err,
                    );
                    this.assignResultModalConfig.set({
                        type: 'error',
                        title: 'Assignment Failed',
                        message: 'Failed to assign license to this playlist. Please try again.',
                    });
                    this.showAssignLicenseModal.set(false);
                    this.showAssignResultModal.set(true);
                    return EMPTY;
                }),
            )
            .subscribe({
                next: (res) => {
                    Logger.log('SinglePlaylistComponent', 'License assigned to playlist', res);
                    this.assignResultModalConfig.set({
                        type: 'success',
                        title: 'License Assigned',
                        message: 'License successfully assigned to this playlist.',
                    });
                    this.showAssignLicenseModal.set(false);
                    this.showAssignResultModal.set(true);
                },
            });
    }

    /**
     * Closes the assign result confirmation modal.
     * Refreshes the playlist when the previous assignment succeeded.
     */
    protected onAssignResultModalClose(): void {
        this.showAssignResultModal.set(false);
        if (this.assignResultModalConfig().type === 'success') {
            this.loadPlaylistDetails();
            this.loadAssignedLicenseIdsAcrossPlaylists();
        }
    }

    /**
     * Maps API response to UI requirements.
     * Each playlist content wraps an array of media entries under `contents`;
     * flatten so every media entry renders as its own grid card.
     * Sorted by `sequence` first — the API doesn't guarantee response order
     * matches playback order.
     *
     * @param playlistContents - The playlistContents array from the API response.
     * @param append - When true, appends the mapped rows to the existing contents list.
     */
    private mapDataToUi(playlistContents: any[], append = false): void {
        const sortedContents = [...playlistContents].sort(
            (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
        );
        const mapped = sortedContents.flatMap((item) => {
            const media: any[] =
                Array.isArray(item.contents) && item.contents.length ? item.contents : [item];

            return media.map((entry) => {
                const fileType = entry.fileType ?? item.contentType;
                const fileSize = Number(entry.fileSize ?? 0);
                const duration = entry.duration ?? item.duration ?? 0;
                return {
                    ...entry,
                    playlistContentId: item.playlistContentId,
                    id: entry.contentId ?? entry.id ?? item.playlistContentId,
                    name: entry.title ?? entry.fileName ?? entry.name ?? 'Untitled',
                    ownerName: entry.ownerName ?? 'N/A',
                    type: getContentType(fileType),
                    src: entry.url ?? entry.thumbnail ?? '',
                    thumbnail: entry.thumbnail ?? entry.url ?? '',
                    size: new FileSizePipe().transform(fileSize),
                    duration: duration.toString(),
                    modified: entry.createdAt ? new Date(entry.createdAt) : undefined,
                    createdAt: entry.createdAt,
                    uuid:
                        typeof entry.metadata === 'object' && entry.metadata
                            ? (entry.metadata.uuid as string | undefined)
                            : undefined,
                } as UiThumbnailContent;
            });
        });

        if (append) {
            this.contents.update((prev) => [...prev, ...mapped]);
        } else {
            this.contents.set(mapped);
        }
    }

    /** ============================ API Calls ================================== */
    /**
     * Loads playlist metadata (name, breadcrumb, assigned licenses) once on mount
     * and after license-assignment success. Contents are fetched separately via
     * {@link loadContents} so they can refresh independently on search.
     */
    private loadPlaylistDetails(): void {
        const playlistId = this.route.snapshot.paramMap.get('id');
        if (!playlistId) {
            return;
        }

        const traits = this.authService.userTraits() as {
            data?: { roleName?: string; userId?: string };
        } | null;
        const role = traits?.data?.roleName;
        const isDealer = role === 'Dealer' || role === 'Dealer Admin';

        this.playlistsService
            .getPlaylistById(playlistId)
            .pipe(
                catchError((err) => {
                    Logger.error('SinglePlaylistComponent', 'Error fetching playlist', err);
                    if (isDealer) {
                        this.router.navigate(['/dashboard']);
                    }
                    return EMPTY;
                }),
            )
            .subscribe((res) => {
                if (res.data) {
                    if (isDealer && res.data.ownerId !== traits?.data?.userId) {
                        this.router.navigate(['/dashboard']);
                        return;
                    }
                    this.playlist.set(res.data);
                    MainLayoutStore.setBreadcrumb(res.data.playlistName);
                    this.loadAssignedLicenses(res.data.assignedLicenses ?? []);
                }
            });
    }

    /**
     * Resolves the assigned license IDs into license keys for display.
     *
     * @param licenseIds - License IDs returned by the playlist endpoint.
     */
    private loadAssignedLicenses(licenseIds: string[]): void {
        if (!licenseIds.length) {
            this.assignedLicenses.set([]);
            this.isLoadingLicenses.set(false);
            return;
        }

        this.isLoadingLicenses.set(true);

        forkJoin(
            licenseIds.map((id) =>
                this.licensesService.getLicenseById(id).pipe(
                    catchError((err: unknown) => {
                        Logger.error(
                            'SinglePlaylistComponent',
                            `Failed to fetch assigned license ${id}`,
                            err,
                        );
                        return of(null);
                    }),
                ),
            ),
        ).subscribe((licenses) => {
            this.assignedLicenses.set(
                licenseIds.map((id, idx) => ({
                    id,
                    licenseKey: licenses[idx]?.licenseKey ?? id,
                })),
            );
            this.isLoadingLicenses.set(false);
        });
    }

    /**
     * Fetches every playlist and aggregates their `assignedLicenses` into a unique list.
     * The result feeds the assign-license modal's exclude list so a license assigned to
     * any playlist cannot be assigned to another.
     */
    private loadAssignedLicenseIdsAcrossPlaylists(): void {
        fetchAllBatch<ApiPlaylist, BaseQueryParams>(
            (params) => this.playlistsService.getPlaylists(params),
            {},
            { pageSize: 1000 },
        )
            .pipe(
                catchError((err: unknown) => {
                    Logger.error(
                        'SinglePlaylistComponent',
                        'Failed to fetch playlists for license exclusion',
                        err,
                    );
                    return of([] as ApiPlaylist[]);
                }),
            )
            .subscribe((playlists) => {
                const ids = new Set<string>();
                for (const playlist of playlists) {
                    for (const licenseId of playlist.assignedLicenses ?? []) {
                        if (licenseId) ids.add(licenseId);
                    }
                }
                this.assignedLicenseIdsAcrossPlaylists.set([...ids]);
            });
    }

    /**
     * Loads playlist contents via the dedicated `/contents` endpoint.
     *
     * @param search - Optional search term used to filter assets by name on the API side.
     * @param append - When true, appends the fetched contents to the existing list.
     */
    protected loadContents(search = '', append = false): void {
        const playlistId = this.route.snapshot.paramMap.get('id');
        if (!playlistId) {
            return;
        }

        this.loadContentsSubscription?.unsubscribe();

        if (append) {
            this.isLoadingMoreContents.set(true);
        }

        const queryParams = this.queryParams();
        const params: BaseQueryParams = {
            ...queryParams,
            ...(search ? { filters: { title: { _ilike: search } } } : {}),
        };

        const loadingSignal = append ? this.isLoadingMoreContents : this.isLoadingContents;

        this.loadContentsSubscription = this.playlistsService
            .getPlaylistContents(playlistId, params)
            .pipe(
                withLoading(loadingSignal),
                catchError((err) => {
                    Logger.error('SinglePlaylistComponent', 'Error fetching contents', err);
                    return EMPTY;
                }),
            )
            .subscribe((res) => {
                if (res.meta?.pagination) {
                    this.paginationMeta.set(res.meta.pagination);
                }

                this.mapDataToUi(res.data?.playlistContents ?? [], append);
                this.isLoadingMoreContents.set(false);
            });
    }

    /**
     * Handles going to the next page of playlist contents.
     */
    protected onNextPage(): void {
        const paginationMeta = this.paginationMeta();
        this.queryParams.update((params) => ({
            ...params,
            page: (params.page ?? 1) + 1,
        }));
    }

    /**
     * Handles resetting and reloading the playlist contents when a new asset is added successfully.
     */
    protected onContentAdded(): void {
        this.queryParams.update((params) => ({ ...params, page: 1 }));
    }
}
